import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Search } from 'lucide-react';
import BeasiswaCard from '../../components/beasiswa/BeasiswaCard';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { loadBeasiswaPosts } from '../../utils/beasiswa';

const Beasiswa = () => {
  const [beasiswaList, setBeasiswaList] = useState([]);
  const [filteredBeasiswaList, setFilteredBeasiswaList] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTag, setSelectedTag] = useState('');

  // Fetch beasiswa data
  useEffect(() => {
    const fetchBeasiswaData = async () => {
      try {
        setIsLoading(true);
        const fetchedBeasiswa = await loadBeasiswaPosts();
        
        // Sort beasiswa by date (newest first)
        const sortedBeasiswa = fetchedBeasiswa.sort((a, b) => 
          new Date(b.date) - new Date(a.date)
        );
        
        setBeasiswaList(sortedBeasiswa);
        setFilteredBeasiswaList(sortedBeasiswa);
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching beasiswa data:', error);
        setError('Gagal memuat data beasiswa. Silakan coba lagi nanti.');
        setIsLoading(false);
      }
    };

    fetchBeasiswaData();
  }, []);

  // Extract all unique tags from beasiswa posts
  const allTags = beasiswaList.reduce((tags, beasiswa) => {
    if (!beasiswa.tags) return tags;
    
    const beasiswaTags = Array.isArray(beasiswa.tags) ? beasiswa.tags : [beasiswa.tags];
    beasiswaTags.forEach(tag => {
      if (!tags.includes(tag)) {
        tags.push(tag);
      }
    });
    
    return tags;
  }, []);

  // Filter beasiswa based on search query and selected tag
  useEffect(() => {
    let result = beasiswaList;
    
    // Filter by search term
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        beasiswa => 
          beasiswa.title.toLowerCase().includes(query) || 
          beasiswa.excerpt.toLowerCase().includes(query) ||
          beasiswa.country.toLowerCase().includes(query) ||
          beasiswa.university.toLowerCase().includes(query) ||
          beasiswa.degree.toLowerCase().includes(query)
      );
    }
    
    // Filter by tag
    if (selectedTag) {
      result = result.filter(beasiswa => {
        const beasiswaTags = Array.isArray(beasiswa.tags) ? beasiswa.tags : [beasiswa.tags];
        return beasiswaTags.some(tag => tag.toLowerCase() === selectedTag.toLowerCase());
      });
    }
    
    setFilteredBeasiswaList(result);
  }, [searchQuery, selectedTag, beasiswaList]);

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  return (
    <div className="bg-white pt-[90px]">
      <Helmet>
        <title>Katalog Beasiswa | Pintar Pilih Kuliah</title>
        <meta name="description" content="Temukan informasi terbaru tentang beasiswa luar negeri untuk melanjutkan studi Anda." />
      </Helmet>
      
      {/* Hero Section */}
      <div className="bg-cream/30 py-16">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-gray-900 mb-4 text-center">
            Katalog Beasiswa
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto text-center">
            Temukan informasi terbaru tentang beasiswa luar negeri untuk melanjutkan studi Anda.
          </p>
        </div>
      </div>
      
      {/* Search and Filter Section */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="relative max-w-xl mx-auto mb-8">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gold focus:border-gold"
            placeholder="Cari beasiswa berdasarkan nama, negara, universitas..."
            value={searchQuery}
            onChange={handleSearchChange}
          />
        </div>
        
        {/* Tags Filter */}
        {allTags.length > 0 && (
          <div className="mb-8 flex flex-wrap items-center gap-2">
            <button
              className={`px-3 py-1 rounded-full text-sm ${
                selectedTag === '' ? 'bg-gold text-white' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
              }`}
              onClick={() => setSelectedTag('')}
            >
              Semua
            </button>
            {allTags.map((tag, index) => (
              <button
                key={index}
                className={`px-3 py-1 rounded-full text-sm ${
                  selectedTag === tag ? 'bg-gold text-white' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                }`}
                onClick={() => setSelectedTag(tag)}
              >
                {tag}
              </button>
            ))}
          </div>
        )}
        
        {isLoading ? (
          <div className="flex justify-center py-20">
            <LoadingSpinner />
          </div>
        ) : error ? (
          <div className="text-center py-20">
            <p className="text-red-500">{error}</p>
          </div>
        ) : filteredBeasiswaList.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredBeasiswaList.map((beasiswa) => (
              <BeasiswaCard key={beasiswa.slug} beasiswa={beasiswa} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <div className="text-gray-400 mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">Tidak Ada Beasiswa Ditemukan</h3>
            <p className="text-gray-500">
              Maaf, tidak ada beasiswa yang sesuai dengan kriteria pencarian Anda.
            </p>
            <button
              onClick={() => {
                setSearchQuery('');
                setSelectedTag('');
              }}
              className="mt-4 inline-flex items-center justify-center bg-gold hover:bg-dark-navy text-white font-medium rounded-md px-5 py-2.5 transition-colors"
            >
              Reset Pencarian
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Beasiswa;